import { Outlet, Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useCompanyLogo } from '@/hooks/useCompanyLogo';
import LoadingOverlay from '../base/LoadingOverlay';
import type { PortalCustomization } from '../../hooks/usePortalCustomization';

interface ClientPortalDesktopLayoutProps {
  token: string;
  clientName: string;
  handleExit: () => void;
  customization?: PortalCustomization;
}

const ACCENT_COLOR_MAP: Record<string, {
  bg: string; bgHover: string; bgLight: string; text: string;
  textLight: string; ring: string; border: string; badgeBg: string; badgeText: string;
  sidebarActive: string; sidebarActiveText: string;
}> = {
  pink:    { bg: 'bg-pink-500', bgHover: 'hover:bg-pink-600', bgLight: 'bg-pink-50', text: 'text-pink-600', textLight: 'text-pink-500', ring: 'ring-pink-500', border: 'border-pink-200', badgeBg: 'bg-pink-100', badgeText: 'text-pink-700', sidebarActive: 'bg-pink-500/20 border-l-2 border-pink-400', sidebarActiveText: 'text-pink-300' },
  rose:    { bg: 'bg-rose-500', bgHover: 'hover:bg-rose-600', bgLight: 'bg-rose-50', text: 'text-rose-600', textLight: 'text-rose-500', ring: 'ring-rose-500', border: 'border-rose-200', badgeBg: 'bg-rose-100', badgeText: 'text-rose-700', sidebarActive: 'bg-rose-500/20 border-l-2 border-rose-400', sidebarActiveText: 'text-rose-300' },
  amber:   { bg: 'bg-[#C9A227]', bgHover: 'hover:bg-[#B8860B]', bgLight: 'bg-[#FEF9EC]', text: 'text-[#B8860B]', textLight: 'text-[#C9A227]', ring: 'ring-[#D4AF37]', border: 'border-[#E6C15A]', badgeBg: 'bg-[#FEF9EC]', badgeText: 'text-[#B8860B]', sidebarActive: 'bg-[#D4AF37]/30 border-l-4 border-[#D4AF37]', sidebarActiveText: 'text-[#E6C15A]' },
  violet:  { bg: 'bg-violet-500', bgHover: 'hover:bg-violet-600', bgLight: 'bg-violet-50', text: 'text-violet-600', textLight: 'text-violet-500', ring: 'ring-violet-500', border: 'border-violet-200', badgeBg: 'bg-violet-100', badgeText: 'text-violet-700', sidebarActive: 'bg-violet-500/20 border-l-2 border-violet-400', sidebarActiveText: 'text-violet-300' },
  emerald: { bg: 'bg-emerald-500', bgHover: 'hover:bg-emerald-600', bgLight: 'bg-emerald-50', text: 'text-emerald-600', textLight: 'text-emerald-500', ring: 'ring-emerald-500', border: 'border-emerald-200', badgeBg: 'bg-emerald-100', badgeText: 'text-emerald-700', sidebarActive: 'bg-emerald-500/20 border-l-2 border-emerald-400', sidebarActiveText: 'text-emerald-300' },
  sky:     { bg: 'bg-sky-500', bgHover: 'hover:bg-sky-600', bgLight: 'bg-sky-50', text: 'text-sky-600', textLight: 'text-sky-500', ring: 'ring-sky-500', border: 'border-sky-200', badgeBg: 'bg-sky-100', badgeText: 'text-sky-700', sidebarActive: 'bg-sky-500/20 border-l-2 border-sky-400', sidebarActiveText: 'text-sky-300' },
  fuchsia: { bg: 'bg-fuchsia-500', bgHover: 'hover:bg-fuchsia-600', bgLight: 'bg-fuchsia-50', text: 'text-fuchsia-600', textLight: 'text-fuchsia-500', ring: 'ring-fuchsia-500', border: 'border-fuchsia-200', badgeBg: 'bg-fuchsia-100', badgeText: 'text-fuchsia-700', sidebarActive: 'bg-fuchsia-500/20 border-l-2 border-fuchsia-400', sidebarActiveText: 'text-fuchsia-300' },
};

const DEFAULT_ACCENT = {
  bg: 'bg-teal-600', bgHover: 'hover:bg-teal-700', bgLight: 'bg-teal-50',
  text: 'text-teal-600', textLight: 'text-teal-500', ring: 'ring-teal-500',
  border: 'border-teal-200', badgeBg: 'bg-teal-100', badgeText: 'text-teal-700',
  sidebarActive: 'bg-teal-500/20 border-l-2 border-teal-400',
  sidebarActiveText: 'text-teal-300',
};

const NAV_ITEMS = (token: string) => [
  { to: `/client/${token}`, label: 'Dashboard', icon: 'ri-home-5-line', exact: true },
  { to: `/client/${token}/sessions`, label: 'Mis Sesiones', icon: 'ri-camera-line', exact: false },
  { to: `/client/${token}/jobs`, label: 'Mis Trabajos', icon: 'ri-folder-image-line', exact: false },
  { to: `/client/${token}/contracts`, label: 'Mis Contratos', icon: 'ri-file-text-line', exact: false },
  { to: `/client/${token}/print-approvals`, label: 'Print Approvals', icon: 'ri-check-double-line', exact: false },
  { to: `/client/${token}/dvd`, label: 'Ver DVD', icon: 'ri-disc-line', exact: false },
  { to: `/client/${token}/descargas`, label: 'Descargas', icon: 'ri-download-cloud-line', exact: false },
  { to: `/client/${token}/invitation`, label: 'Invitación', icon: 'ri-mail-send-line', exact: false },
  { to: `/client/${token}/pose-guides`, label: 'Guías de Poses', icon: 'ri-book-open-line', exact: false },
  { to: `/client/${token}/photobooks`, label: 'Photobooks', icon: 'ri-book-2-line', exact: false },
];

export default function ClientPortalDesktopLayout({ token, clientName, handleExit, customization }: ClientPortalDesktopLayoutProps) {
  const location = useLocation();
  const companyLogo = useCompanyLogo();
  const [isNavigating, setIsNavigating] = useState(false);
  const prevPathRef = useRef(location.pathname);

  const accent = useMemo(() => {
    if (customization?.accentColor && ACCENT_COLOR_MAP[customization.accentColor]) {
      return ACCENT_COLOR_MAP[customization.accentColor];
    }
    return DEFAULT_ACCENT;
  }, [customization?.accentColor]);

  const bgImage = customization?.backgroundUrl || null;

  useEffect(() => {
    if (location.pathname !== prevPathRef.current) {
      setIsNavigating(true);
      prevPathRef.current = location.pathname;
      const timer = setTimeout(() => setIsNavigating(false), 600);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  const isActive = (item: { to: string; exact: boolean }) => {
    if (item.exact) {
      return location.pathname === item.to || location.pathname === item.to + '/';
    }
    return location.pathname.startsWith(item.to);
  };

  const navItems = NAV_ITEMS(token);

  return (
    <>
      {isNavigating && <LoadingOverlay message="Cargando..." />}

      {/* Background */}
      {bgImage ? (
        <>
          <div className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${bgImage})` }}></div>
          <div className="fixed inset-0 z-[1] bg-gradient-to-r from-black/70 via-black/40 to-black/20"></div>
        </>
      ) : (
        <div className="fixed inset-0 z-0 bg-gray-50"></div>
      )}

      {/* Layout: sidebar + content */}
      <div className="relative z-10 flex min-h-screen">

        {/* ── SIDEBAR ── */}
        <aside className={`w-60 flex-shrink-0 flex flex-col fixed top-0 left-0 h-screen z-40 ${
          bgImage
            ? 'bg-black/40 backdrop-blur-xl border-r border-white/10'
            : 'bg-white border-r border-gray-200'
        }`}>

          {/* Logo & brand */}
          <div className={`px-5 py-5 flex items-center gap-3 border-b ${bgImage ? 'border-white/10' : 'border-gray-100'}`}>
            <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 bg-white/10 flex items-center justify-center">
              <img
                src={companyLogo || 'https://fotosyvideosantarosa.com/storage/2025/12/fyvnueva2.png'}
                alt="Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="min-w-0">
              <p className={`text-xs font-bold leading-tight truncate ${bgImage ? 'text-white' : 'text-gray-900'}`}>
                Fotos y Video
              </p>
              <p className={`text-[10px] truncate ${bgImage ? 'text-white/60' : 'text-gray-400'}`}>
                Santa Rosa
              </p>
            </div>
          </div>

          {/* Client info */}
          <div className={`px-5 py-3 border-b ${bgImage ? 'border-white/10' : 'border-gray-100'}`}>
            <div className="flex items-center gap-2.5">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold ${bgImage ? 'bg-white/20 text-white' : `${accent.badgeBg} ${accent.badgeText}`}`}>
                {clientName ? clientName.charAt(0).toUpperCase() : 'C'}
              </div>
              <div className="min-w-0">
                <p className={`text-xs font-semibold truncate ${bgImage ? 'text-white' : 'text-gray-800'}`}>{clientName || 'Cliente'}</p>
                <p className={`text-[10px] truncate ${bgImage ? 'text-white/50' : 'text-gray-400'}`}>Portal de Cliente</p>
              </div>
            </div>
          </div>

          {/* Nav links */}
          <nav className="flex-1 overflow-y-auto py-3 px-2">
            <div className="space-y-0.5">
              {navItems.map((item) => {
                const active = isActive(item);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                      active
                        ? bgImage
                          ? `${accent.sidebarActive} ${accent.sidebarActiveText}`
                          : `${accent.badgeBg} ${accent.badgeText}`
                        : bgImage
                          ? 'text-white/70 hover:text-white hover:bg-white/10'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <i className={`${item.icon} text-lg flex-shrink-0`}></i>
                    <span className="truncate">{item.label}</span>
                    {active && (
                      <div className={`ml-auto w-1.5 h-1.5 rounded-full flex-shrink-0 ${bgImage ? 'bg-white/60' : accent.bg}`}></div>
                    )}
                  </Link>
                );
              })}
            </div>

            {/* Divider */}
            <div className={`my-3 border-t ${bgImage ? 'border-white/10' : 'border-gray-100'}`}></div>

            {/* WhatsApp */}
            <a
              href="https://wa.me/17078066168"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer text-green-500 hover:bg-green-500/10"
            >
              <i className="ri-whatsapp-line text-lg flex-shrink-0"></i>
              <span>Contactar</span>
            </a>
          </nav>

          {/* Exit button at bottom */}
          <div className={`px-3 py-4 border-t ${bgImage ? 'border-white/10' : 'border-gray-100'}`}>
            <button
              onClick={handleExit}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                bgImage
                  ? 'text-white/60 hover:text-white hover:bg-white/10'
                  : 'text-gray-500 hover:text-red-600 hover:bg-red-50'
              }`}
            >
              <i className="ri-logout-box-line text-lg"></i>
              <span>Cerrar sesión</span>
            </button>
          </div>
        </aside>

        {/* ── MAIN CONTENT ── */}
        <div className="flex-1 ml-60 flex flex-col min-h-screen">
          <main className="flex-1">
            <Outlet />
          </main>

          {/* Footer */}
          <footer className={`border-t px-6 py-4 ${bgImage ? 'bg-black/20 border-white/10' : 'bg-white border-gray-100'}`}>
            <p className={`text-xs text-center ${bgImage ? 'text-white/40' : 'text-gray-400'}`}>
              Portal seguro y privado · Fotos y Video Santa Rosa
            </p>
          </footer>
        </div>
      </div>
    </>
  );
}